export type PropertyListItem = {
  id: number
  // "url": "80128-113-west-allegheny-street"
  // "customerId": 82645
  address: string
  cityStateZip: string
  tags: string
  // "isActive": true
  // "nicknameAndAddress": "Bag End, 113 West Allegheny Street"
  nickname: string
}

export const filterProperties = (properties: PropertyListItem[], search: string): PropertyListItem[] => {
  const searchTerm = search.toLowerCase()
  return properties.map(x => ({
    property: x,
    search: [
      x.nickname,
      x.address,
      x.cityStateZip,
      x.tags
    ].join(' ').toLowerCase()
  }))
    .filter(x => x.search.includes(searchTerm))
    .map(x => x.property)
}

export type Property = {
  id: number
  customerId: number
  isActive: boolean
  nickname: string
  address1: string
  address2: string
  county: string
  city: string
  state: string
  countryCode: string
  zip: string
  latitude?: number
  longitude?: number
  tags: string
  tagsArray: {text: string}[]
  notes: string
  primaryPhotoId?: string | null
  primaryPhotoUrl: string
  displayName: string
  attachments?: number
  projects: number
}

export const createNewProperty = (customerId: number): Property => ({
  id: 0,
  customerId,
  isActive: true,
  nickname: '',
  address1: '',
  address2: '',
  county: '',
  city: '',
  state: '',
  countryCode: 'US',
  zip: '',
  tags: '',
  tagsArray: [],
  notes: '',
  primaryPhotoId: '',
  primaryPhotoUrl: '',
  displayName: 'New Property',
  projects: 0
})

export type PropertyFormValues = {
  isActive: boolean
  nickname: string
  address1: string
  address2: string
  city: string
  zip: string
  county: string
  countryCode: string
  state: string
  tags: string[],
  notes: string
}

export const propertyFormValidationRules = {
  nickname: {required: false, maxLength: 45 },
  address1: {required: true, maxLength: 45 },
  address2: {required: false, maxLength: 45 },
  city: {required: true, maxLength: 35 },
  zip: {required: true, maxLength: 10 },
  county: {required: false, maxLength: 35 },
  countryCode: {required: true, maxLength: null },
  state: {required: true, maxLength: 35 }
}

export const createPropertyFormValues = (property?: Property): PropertyFormValues => {
  if (property) {
    return {
      isActive: property.isActive,
      nickname: property.nickname,
      address1: property.address1,
      address2: property.address2,
      city: property.city,
      zip: property.zip,
      county: property.county,
      countryCode: property.countryCode,
      state: property.state,
      tags: property.tagsArray.map(x => x.text),
      notes: property.notes
    }
  }

  return {
    isActive: true,
    nickname: '',
    address1: '',
    address2: '',
    city: '',
    zip: '',
    county: '',
    countryCode: 'US',
    state: '',
    tags: [],
    notes: ''
  }
}

export const updatePropertyFromFormValues = (property: Property, values: PropertyFormValues): Property => {
  const tagsArray = values.tags.map(x => ({text: x}))
  const tags = values.tags.join(', ')
  return {...property, ...values, tagsArray, tags}
}

export const updateLocation = (property: Property, location: {latitude: number, longitude: number}): Property => {
  return {
    ...property,
    latitude: location.latitude,
    longitude: location.longitude
  }
}

export const propertyHasChanges = (serverProperty: Property | undefined, editingProperty: Property): boolean => {
  if (!serverProperty) {
    return false
  }
  return JSON.stringify(serverProperty) !== JSON.stringify(editingProperty)
}

export const isValidForSave = (property: Property): boolean => {
  for (const [key, value] of Object.entries(propertyFormValidationRules)) {
    const field = (property as any)[key]
    if (value.required && !field) {
      return false
    }
    if (value.maxLength && field?.length > value.maxLength) {
      return false
    }
  }
  return true
}

export const getLocation = (property: Property): {latitude: number, longitude: number} | undefined => {
  if (property.latitude && property.longitude) {
    return {latitude: property.latitude, longitude: property.longitude}
  }
  return undefined
}

export const updatePrimaryPhoto = (property: Property, primaryPhotoId: string | null | undefined): Property => {
  return {...property, primaryPhotoId}
}